import * as React from 'react';
import {
    LoadingIndicator,
    UserMultiSelect,
    PVAPage,
    PonderaModal,
    LoadingIndicatorMini,
    PVAActionsButtonGroup,
    PVATeamSelect,
    GroupChart
} from '../../../components';
import {InclinationType, MidRangeType, ReportType} from '../../../enums';
import { UserDataService, ReportDataService, CharacteristicDataService, CharacteristicGroupDataService, Auth } from '../../../services';
import { User, CharacteristicGroup, Characteristic, CharacteristicText, SurveyVersion, CharacteristicGroupCharacteristic, CharacteristicGroupSubGroupCharacteristic, UserCharacteristicToggle, Team, UserPermissions } from '../../../models';
import { hasPermission } from '../../../utilities/User';
import { BaseRouteProps } from '../../../interfaces';
import { UserStore, FilterStore } from '../../../stores';
import { Roles } from '../../../stores/Constants';
import { inject, observer } from 'mobx-react';
import { computed } from 'mobx';
import './ComparisonReport.css';


interface ComparisonReportState {
    selectedUserData: any[];
    users: User[];
    reportType: ReportType;
    characteristicGroups: CharacteristicGroup[];
    characteristics: Characteristic[];
    orgId: number;
    teamId: number;
    surveyId: number;
    surveyVersionId: number;
    surveyVersions: SurveyVersion[];
    selectedUserIds: number[];
    loading: boolean;
    miniLoader: boolean;
    pdfLoader: boolean;
    markedCharacteristics: UserCharacteristicToggle[];
}

interface ComparisonReportProps extends BaseRouteProps<{}> {
    UserStore?: UserStore;
    FilterStore?: FilterStore;
}

@inject('UserStore', 'FilterStore') @observer
export class ComparisonReport extends React.Component<ComparisonReportProps, ComparisonReportState> {
    private userDataService = new UserDataService(this.props.notificationService);
    private reportDataService = new ReportDataService(this.props.notificationService);
    private characteristicDataService = new CharacteristicDataService(this.props.notificationService);
    private characteristicGroupDataService = new CharacteristicGroupDataService(this.props.notificationService);
    private pageTop: any;
    private groupEls: any[];

    @computed get userRole() {
        return this.props.UserStore!.userInfo.userRole;
    }

    @computed get users() {
        if (this.props.FilterStore && this.props.FilterStore.users) {
            // Filter to only show users who have completed the current survey version
            // AND are not Foundations users
            return this.props.FilterStore.users.filter((user: User) => {
                if (hasPermission(user.userPermissions, UserPermissions.Foundations)) {
                    return false;
                }

                if (!this.surveyVersion) {
                    return user.userStyles && user.userStyles.length > 0;
                }

                // Check if user has a userStyle for the current survey version
                return user.userStyles && user.userStyles.some(userStyle => {
                    return userStyle.style &&
                        userStyle.style.surveyVersion &&
                        userStyle.style.surveyVersion.id === this.surveyVersion!.id;
                });
            });
        }
        return [];
    }

    @computed get survey() {
        if (this.props.FilterStore && this.props.FilterStore.survey) {
            return this.props.FilterStore.survey;
        }
        return null;
    }

    @computed get surveyVersion() {
        if (this.props.FilterStore && this.props.FilterStore.surveyVersion) {
            return this.props.FilterStore.surveyVersion;
        }
        return null;
    }

    @computed get organization() {
        if (this.props.FilterStore && this.props.FilterStore.organization) {
            return this.props.FilterStore.organization;
        }
        return null;
    }

    @computed get team() {
        if (this.props.FilterStore && this.props.FilterStore.team) {
            return this.props.FilterStore.team;
        }
        return null;
    }

    @computed get userPermissions() {
        return this.props.UserStore!.userInfo.userPermissions;
    }

    @computed get teamsThisUserManages() {
        const auth = new Auth();
        const managedTeams = auth.managedTeams();
        if (this.props.FilterStore && this.props.FilterStore.teams) {
            return this.props.FilterStore.teams.filter(t => managedTeams.includes(t.id));
        }
        return [];
    }

    @computed get teams() {
        if (this.props.FilterStore && this.props.FilterStore.teams && this.props.FilterStore.teams.length > 0) {
            return this.props.FilterStore.teams;
        }
        return this.teamsThisUserManages;
    }

    @computed get surveyReportType() {
        if (this.props.FilterStore && this.props.FilterStore.surveyReportType) {
            return this.props.FilterStore.surveyReportType;
        }
        return null;
    }

    @computed get teamId() {
        if (this.props.FilterStore && this.props.FilterStore.team) {
            return this.props.FilterStore.team.id;
        }
        if (this.props.FilterStore && this.props.FilterStore.teamId) {
            return this.props.FilterStore.teamId;
        }
        return 0;
    }

    constructor(props: ComparisonReportProps) {
        super(props);

        this.groupEls = [];
        // this.scrollUpdate = undefined;

        this.state = {
            selectedUserData: [],
            users: [],
            characteristicGroups: [],
            characteristics: [],
            reportType: ReportType.Standard,
            orgId: 0,
            teamId: 0,
            surveyId: 0,
            surveyVersionId: 0,
            surveyVersions: [],
            selectedUserIds: [],
            loading: false,
            miniLoader: false,
            pdfLoader: false,
            markedCharacteristics: []
        };
    }

    private update() {
        if (this.userRole !== Roles.User) {
            if (this.organization && this.organization.id !== this.state.orgId) {
                var orgId = this.organization.id;
                this.setState({
                    orgId: orgId,
                    loading: true
                });

                this.userDataService.getByOrganizationLight(orgId).then((users: User[]) => {
                    users.sort((a: User, b: User) => {
                        if (a.lastName > b.lastName) {
                            return 1;
                        } else if (a.lastName < b.lastName) {
                            return -1;
                        }

                        if (a.firstName > b.firstName) {
                            return 1;
                        } else if (a.firstName < b.firstName) {
                            return -1;
                        }

                        return 0;
                    });
                    this.setState({
                        users: users,
                        orgId: orgId,
                        selectedUserData: [],
                        selectedUserIds: [],
                        loading: false
                    });
                });
            }
        }

        if (this.team && this.team.id !== this.state.teamId) {
            this.setState({
                teamId: this.team.id,
                selectedUserData: [],
                selectedUserIds: []
            });
        } else if (!this.team && this.state.teamId) {
            this.setState({
                teamId: 0,
                selectedUserData: [],
                selectedUserIds: []
            });
        }

        if (this.survey !== null && this.state.surveyId !== this.survey.id) {
            var survey = this.survey;

            this.setState({
                surveyId: survey.id
            });
        }

        if (this.surveyVersion != null && this.state.surveyVersionId !== this.surveyVersion.id) {

            this.setState({
                surveyVersionId: this.surveyVersion.id,
                loading: true
            });

            if (this.surveyVersion) {
                this.characteristicGroupDataService.getAllBySurveyVersion(this.surveyVersion).then((characteristicGroups: CharacteristicGroup[]) => {
                    characteristicGroups = characteristicGroups.filter((cg: CharacteristicGroup) => {
                        if (cg.secondaryReportID) {
                            return false;
                        }
                        return true;
                    });

                    this.setState({
                        characteristicGroups: characteristicGroups
                    });

                    if (this.surveyVersion) {
                        this.characteristicDataService.getAllBySurveyVersion(this.surveyVersion).then((characteristics: Characteristic[]) => {
                            this.setState({
                                characteristics: characteristics,
                                loading: false
                            });
                        });
                    }
                });
            }
           // });
        }
    }

    componentWillReact() {
        this.update();
    }

    componentDidMount() {
        this.update();
    }

    toggleUserSelect(e: any) {
        var id = Number(e.target.value);
        var ids = this.state.selectedUserIds;
        var ind = ids.indexOf(id);
        if (ind > -1) {
            ids.splice(ind, 1);
            var dataInd = this.state.selectedUserData.findIndex((data: any) => {
                return data.userId === id;
            });
            if (dataInd > -1) {
                var d = this.state.selectedUserData;
                d.splice(dataInd, 1);
                this.setState({
                    selectedUserData: d
                });
            }
        } else {
            ids.push(id);            
            if (this.surveyVersion && this.survey) {
                this.setState({
                    miniLoader: true
                });
                this.reportDataService.getIndividualReportDataLatest(id, this.surveyVersion.id).then((reportData) => { // this.survey.id, , this.surveyVersion.id
                    if (ids.indexOf(id) > -1) { // make sure it's still selected
                        this.state.selectedUserData.push({
                            data: reportData,
                            userId: id
                        });
                        this.setState({
                            selectedUserData: this.state.selectedUserData
                        });
                    }
                    this.setState({
                        miniLoader: false
                    });
                }, (err: any) => {
                    // User has no survey data - remove from selection
                    const user = this.users.find(u => u.id === id);
                    const userName = user ? `${user.firstName} ${user.lastName}` : 'User';
                    this.props.notificationService.addWarning(`${userName} has no survey data and cannot be compared.`);

                    var currentIds = this.state.selectedUserIds;
                    var currentInd = currentIds.indexOf(id);
                    if (currentInd > -1) {
                        currentIds.splice(currentInd, 1);
                    }

                    this.setState({
                        selectedUserIds: currentIds,
                        miniLoader: false
                    });
                });
            }
        }
        this.setState({
            selectedUserIds: ids
        });
    }

    tooltip(c: Characteristic) {
        if (!c.characteristicTexts) {
            return '';
        }

        var result = '';
        c.characteristicTexts.map((ct: CharacteristicText) => {
            if (ct.textType === 'tooltip') {
                result = ct.value;
            }
        });

        return result;
    }

    filteredCharacteristics(groupData: any, characteristic: Characteristic) {
        return groupData.data.filter((x: any) => {
            return x.characteristicID === characteristic.id || x.previousCharacteristicID === characteristic.id;
        });
    }

    teamChange(e: any) {
        if (this.props.FilterStore) {
            var id = Number(e.target.value);
            var team = this.teams.find((t: Team) => {
                return t.id === id;
            });
            if (team) {
                this.props.FilterStore.setTeam(team);
            } else {
                this.props.FilterStore.setTeamID(id);
            }
            this.setState({
                selectedUserData: [],
                selectedUserIds: []
            });
        }
    }

    private exportPDF() {
        if (this.state.selectedUserData) {
            var userIds: number[] = [];
            this.state.selectedUserData.map((data: any) => {
                userIds.push(data.userId);
            });

            this.setState(
                {
                    pdfLoader: true
                });
            this.reportDataService.getComparisonReportDataPDF(this.state.surveyVersionId, userIds, this.state.markedCharacteristics, 'ComparisonReport', this.surveyReportType ? this.surveyReportType.id : undefined)
                .then(
                    (reportData: any) => {
                    this.setState(
                        {
                            pdfLoader: false
                        });
                },  (err: any) => {
                    this.setState(
                        {
                            pdfLoader: false
                        });
                });
        }
    }
    
    public render() {
        const actionGroups: PVAActionsButtonGroup[] = [
            {
                header: 'Export Options',
                items: [
                    {
                        id: 'pdf-export',
                        name: 'PDF Export',
                        icon: 'Icon awesome-file-pdf.svg',
                        callback: () => this.exportPDF()
                    }
                ]
            }
        ];

        let defaultOptions: any[] = [{
            id: 0,
            value: '- Select -'
        }];

        if (!this.props.UserStore!.userRoleIn([Roles.Manager, Roles.User]) || this.props.UserStore!.checkUserPermission(UserPermissions.ViewAllUsers)) {
            defaultOptions.push({
                id: -100,
                value: 'All Users'
            });
        }
        if (!this.props.UserStore!.userRoleIn([Roles.Manager, Roles.User]) || this.props.UserStore!.checkUserPermission(UserPermissions.ViewAllCandidates)) {
            defaultOptions.push({
                id: -200,
                value: 'All Candidates'
            });
        }
        if (!this.props.UserStore!.userRoleIn([Roles.Manager, Roles.User]) || this.props.UserStore!.checkUserPermission(UserPermissions.ViewAllFormerEmployees)) {
            defaultOptions.push({
                id: -300,
                value: 'All Former Employees'
            });
        }

        let offset = 0;
        return (
            <PVAPage ref={(e: any) => this.pageTop = e}>
                <PVAPage.Header actions={actionGroups}>
                    <PVAPage.Header.Title>
                        <PVAPage.Header.Title.Text>
                            <span>PVA Comparison Report <span className="trademark">TM</span></span> <span className="light"> for </span>
                            <PVATeamSelect defaultOptions={defaultOptions} options={this.teams} selected={this.teamId} onChange={(e: any) => { this.teamChange(e); }} />
                            <div><UserMultiSelect options={this.users} selected={this.state.selectedUserIds} onChange={(e: any) => this.toggleUserSelect(e)} FilterStore={this.props.FilterStore} /></div>
                            {this.team && <span><span className="light"> in </span>'{this.team.name}'</span>}
                            {this.state.miniLoader && <LoadingIndicatorMini />}
                        </PVAPage.Header.Title.Text>
                    </PVAPage.Header.Title>
                </PVAPage.Header>

                {this.state.loading && <PVAPage.Body><LoadingIndicator /></PVAPage.Body>}
                {this.state.pdfLoader && <PVAPage.Body><LoadingIndicator showPdfDescription={true} showBackground={true} showInPanel={true}/></PVAPage.Body>}
                {!this.state.loading && !this.state.pdfLoader && <PVAPage.Body>
                    <div className="comparisonReport-group-charts">
                    {this.state.characteristicGroups.filter((x: CharacteristicGroup) => {
                        if (x.characteristicGroupDisplayType !== 'normal') {
                            return false;
                        }
                        if (
                            (!x.characteristicGroupCharacteristics || x.characteristicGroupCharacteristics.length === 0) &&                            
                            (!x.characteristicGroupSubGroups || x.characteristicGroupSubGroups.length === 0)
                         ) {
                            return false;
                        }
                        if (!this.surveyReportType) {
                            return true;
                        }
                        return x.surveyReportTypeID === this.surveyReportType.id;
                    }).map((group: CharacteristicGroup, groupIndex: number) => {
                        const props = {
                            show: group.characteristicGroupTexts && group.characteristicGroupTexts.length > 0 ? group.characteristicGroupTexts[0].showModal : false,
                            onHide: this.toggleGroupModal.bind(this, groupIndex),
                            title: group.name,
                            content: group.characteristicGroupTexts && group.characteristicGroupTexts.length > 0 ? group.characteristicGroupTexts[0].value : '',
                            topic: group.subtitle
                        };

                        const groupData = {
                            id: group.id,
                            characteristicGroupID: group.id,
                            name: group.name,
                            subtitle: group.subtitle,
                            description: group.characteristicGroupTexts && group.characteristicGroupTexts.length > 0 ? group.characteristicGroupTexts[0].value : '',
                            data: this.filteredSortedCharacteristics(group).map(c => ({
                                id: c.id,
                                characteristicID: c.id,
                                name: c.displayName || c.name,
                                helpText: this.getText(c.characteristicTexts, 'higH_TEXT'),
                                description: this.getText(c.characteristicTexts, 'tooltip'),
                                tooltip: this.tooltip(c),
                                inclinationType: InclinationType.confidentAndBold,
                                value: 0, // value is not used in comparison mode from this array
                                midrangeLow: 0,
                                midrangeHigh: 0,
                                midrangeLowOrg: 0,
                                midrangeHighOrg: 0,
                                midrangeLowAll: 0,
                                midrangeHighAll: 0,
                                teamMidRanges: []
                            })),
                            subGroups: group.characteristicGroupSubGroups ? group.characteristicGroupSubGroups.map(sg => ({
                                name: sg.name,
                                sort: sg.sort,
                                about: sg.characteristicGroupSubGroupTexts && sg.characteristicGroupSubGroupTexts.length > 0 ? sg.characteristicGroupSubGroupTexts[0].value : '',
                                data: this.filteredSortedCharacteristics(group).filter(c => c.characteristicGroupSubGroupCharacteristics && c.characteristicGroupSubGroupCharacteristics.some(cgsgc => cgsgc.characteristicGroupSubGroupID === sg.id)).map(c => ({
                                    id: c.id,
                                    characteristicID: c.id,
                                    name: c.displayName || c.name,
                                    helpText: this.getText(c.characteristicTexts, 'higH_TEXT'),
                                    description: this.getText(c.characteristicTexts, 'tooltip'),
                                    tooltip: this.tooltip(c),
                                    inclinationType: InclinationType.confidentAndBold,
                                    value: 0,
                                    midrangeLow: 0,
                                    midrangeHigh: 0,
                                    midrangeLowOrg: 0,
                                    midrangeHighOrg: 0,
                                    midrangeLowAll: 0,
                                    midrangeHighAll: 0,
                                    teamMidRanges: []
                                }))
                            })) : [],
                            comparisons: group.characteristicCompares || []
                        };

                        const currentOffset = offset;
                        const characteristics = this.filteredSortedCharacteristics(group);
                        offset += characteristics.length;

                        return (
                            <div className="comparisonReport-group-chart-container" key={`group-${groupIndex}`} ref={(e) => this.groupEls[groupIndex] = e}>
                                <GroupChart 
                                    groupData={groupData} 
                                    reportType={ReportType.Standard} 
                                    midRangeType={MidRangeType.All} 
                                    teamID={this.teamId} 
                                    index={groupIndex + 1} 
                                    offset={currentOffset} 
                                    allowToggle={this.props.UserStore!.checkUserPermission(UserPermissions.ScoreBarColorChange)}
                                    comparisonData={this.state.selectedUserData}
                                    onToggle={(charId, userId) => this.charToggle(charId, userId)}
                                    toggleState={(charId, userId) => this.toggleState(charId, userId)}
                                />
                                <PonderaModal {...props} />
                            </div>
                        );
                    })}
                    </div>
                </PVAPage.Body>}
                <PVAPage.Footer />
            </PVAPage>);
    }

    charToggle(characteristicId: number, userId: number) {
        var markedCharacteristics = this.state.markedCharacteristics;
        var ind = markedCharacteristics.findIndex(m => {
            return m.characteristicId === characteristicId && m.userId === userId;
        });

        if (ind > -1) {
            markedCharacteristics.splice(ind, 1);
        } else {
            markedCharacteristics.push({
                characteristicId: characteristicId,
                userId: userId
            });
        }
        this.setState({
            markedCharacteristics: markedCharacteristics
        });
    }

    toggleState(characteristicId: number, userId: number) {
        return this.state.markedCharacteristics.findIndex(m => {
            return m.characteristicId === characteristicId && m.userId === userId;
        }) > -1;
    }

    toggleGroupModal(ind: number) {
        if (this.state.characteristicGroups[ind] && this.state.characteristicGroups[ind].characteristicGroupTexts && this.state.characteristicGroups[ind].characteristicGroupTexts.length > 0) {
            this.state.characteristicGroups[ind].characteristicGroupTexts[0].showModal = !this.state.characteristicGroups[ind].characteristicGroupTexts[0].showModal;
            this.forceUpdate();
        }
    }

    toggleModal(c: Characteristic) {
        if (c && c.characteristicTexts && c.characteristicTexts.length > 0) {
            c.characteristicTexts[0].showModal = !c.characteristicTexts[0].showModal;
            this.forceUpdate();
        }
    }

    getText(characteristicTexts: CharacteristicText[], key: string) {
        var charText = characteristicTexts.find((ct: CharacteristicText) => {
            return ct.textType === key;
        });
        if (charText) {
            return charText.value;
        }
        return '';
    }

    filteredSortedCharacteristics(group: CharacteristicGroup) {
        return this.state.characteristics.filter((x: Characteristic) => {
            var g = x.characteristicGroupCharacteristics.find((cgc: CharacteristicGroupCharacteristic) => {
                return cgc.characteristicGroupID === group.id;
            });
            if (g) {
                return true;
            }

            // Look to see if the characteristic is in a sub group of this group
            if (x.characteristicGroupSubGroupCharacteristics) {
                var sg = x.characteristicGroupSubGroupCharacteristics.find((cgsgc: CharacteristicGroupSubGroupCharacteristic) => {
                    return cgsgc.characteristicGroupSubGroup && cgsgc.characteristicGroupSubGroup.characteristicGroupID === group.id;
                });
                if (sg) {
                    return true;
                }
            }

            return x.characteristicGroupID === group.id;
        }).sort((c1: Characteristic, c2: Characteristic) => {
            var sort1 = c1.sort;
            var sort2 = c2.sort;

            var cgc1 = c1.characteristicGroupCharacteristics.find((c: CharacteristicGroupCharacteristic) => {
                return c.characteristicGroupID === group.id;
            });
            var cgc2 = c2.characteristicGroupCharacteristics.find((c: CharacteristicGroupCharacteristic) => {
                return c.characteristicGroupID === group.id;
            });

            var sgcgc: CharacteristicGroupSubGroupCharacteristic | undefined;
            if (cgc1) {
                sort1 = cgc1.sort;
            } else {
                // look for subgroup
                sgcgc = c1.characteristicGroupSubGroupCharacteristics.find((cgc: CharacteristicGroupSubGroupCharacteristic) => {
                    return cgc.characteristicGroupSubGroup && cgc.characteristicGroupSubGroup.characteristicGroupID === group.id;
                });
                if (sgcgc) {
                    sort1 = sgcgc.characteristicGroupSubGroup.sort + sgcgc.sort;
                }
            }
            if (cgc2) {
                sort2 = cgc2.sort;
            } else {
                // look for subgroup
                sgcgc = c2.characteristicGroupSubGroupCharacteristics.find((cgc: CharacteristicGroupSubGroupCharacteristic) => {
                    return cgc.characteristicGroupSubGroup && cgc.characteristicGroupSubGroup.characteristicGroupID === group.id;
                });
                if (sgcgc) {
                    sort2 = sgcgc.characteristicGroupSubGroup.sort + sgcgc.sort;
                }
            }

            if (sort1 > sort2) {
                return 1;
            } else if (sort1 < sort2) {
                return -1;
            }
            return 0;
        });
    }
}



// WEBPACK FOOTER //
// ./src/components/reports/comparison/ComparisonReport.tsx